const ENVIRONMENT = {
  local: "local",
  ci: "ci",
  development: "development",
  production: "production",
} as const;

type Environment = (typeof ENVIRONMENT)[keyof typeof ENVIRONMENT];

/**
 * Determine the current environment.
 * Priority: VITE_ENV > VITE_APP_ENV > infer from VITE_API_BASE_URL > "local"
 */
function inferEnvironment(): Environment {
  const explicit =
    import.meta.env.VITE_ENV || import.meta.env.VITE_APP_ENV;
  if (explicit && explicit in ENVIRONMENT) return explicit as Environment;

  // Infer from the api host: if it points at a real domain, assume production
  const host = import.meta.env.VITE_API_BASE_URL || "";
  if (
    host &&
    !host.startsWith("localhost") &&
    !host.startsWith("127.0.0.1") &&
    !host.startsWith("0.0.0.0")
  ) {
    return ENVIRONMENT.production;
  }

  return ENVIRONMENT.local;
}

const currentEnvironment = inferEnvironment();

const baseConfig = {
  env: currentEnvironment,
  isCi: currentEnvironment === ENVIRONMENT.ci,
};

/**
 * Resolve the API hostname.
 * Falls back to "localhost:3000" only when no value is provided at all.
 */
function resolveApiHost(): string {
  const raw = import.meta.env.VITE_API_BASE_URL;
  if (raw) return raw;

  return currentEnvironment === ENVIRONMENT.production
    ? "api.superhero.pw"
    : "localhost:3000";
}

const apiHost = resolveApiHost();

/**
 * Determine if HTTPS should be used for API calls.
 * True when the environment is production OR the apiHost clearly points to
 * a remote domain (not localhost / 127.0.0.1).
 */
function resolveShouldUseHttps(): boolean {
  if (currentEnvironment === ENVIRONMENT.production) return true;

  // Explicit protocol in the host string overrides
  if (apiHost.startsWith("https://")) return true;
  if (apiHost.startsWith("http://")) return false;

  // Remote hostname → HTTPS
  if (
    !apiHost.startsWith("localhost") &&
    !apiHost.startsWith("127.0.0.1") &&
    !apiHost.startsWith("0.0.0.0")
  ) {
    return true;
  }

  return false;
}

export const ENVIRONMENT_CONFIG = {
  ...baseConfig,
  apiHost,
  shouldUseHttps: resolveShouldUseHttps(),
  isProduction: currentEnvironment === ENVIRONMENT.production,
  isMonitored: currentEnvironment === ENVIRONMENT.production,
};

export const getUrl = (host: string, path?: string): string => {
  const protocol = ENVIRONMENT_CONFIG.shouldUseHttps ? "https" : "http";
  const endpoint = path || "";
  return `${protocol}://${host}${endpoint}`;
};

/**
 * Build a full API URL from a path.
 * The API host is determined by VITE_API_BASE_URL env var.
 */
export const getApiUrl = (path?: string): string => {
  return getUrl(ENVIRONMENT_CONFIG.apiHost, path);
};

export const isLocalOrDevEnvironment = () => {
  return (
    currentEnvironment === ENVIRONMENT.local ||
    currentEnvironment === ENVIRONMENT.development
  );
};